
import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import Slider from "react-slick";
import { Link } from "react-router-dom";
import { Modal } from "react-responsive-modal";

import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Check,
  Expand,
  Gamepad2,
  Layers3,
  Maximize2,
  Network,
  ShieldCheck,
  Sparkles,
  WalletCards,
  BrainCircuit,
  X
} from "lucide-react";
import "./Styles.css";
import { useWindowWidth } from "@react-hook/window-size";

const R2_BASE =
  "https://pub-93a1c3f08d9142d8b81cbf054b4cbe0d.r2.dev";

const r2Image = filename => `${R2_BASE}/${filename}`;

/* =====================================================
   PRODUCT GALLERY DATA
   ===================================================== */

const games = [
  {
    name: "Blackjack",
    file: "bj-fin.png",
    category: "tables",
    description: "Classic blackjack, reimagined for a modern casino experience."
  },
  {
    name: "Baccarat",
    file: "bacccomputer.png",
    category: "tables",
    description: "A refined digital baccarat table with a modern interface."
  },
  {
    name: "Immersive Baccarat",
    file: "baccimmersive.png",
    category: "tables",
    description: "A more immersive visual approach to the traditional baccarat experience."
  },
  {
    name: "Craps",
    file: "crapscraps.png",
    category: "tables",
    description: "Casino dice gameplay presented through a digital table experience."
  },
  {
    name: "Roulette",
    file: "roulroulredone.png",
    category: "tables",
    description: "The roulette experience with a purpose-built betting interface."
  },
  {
    name: "Casino War",
    file: "warwarwar.png",
    category: "tables",
    description: "Fast, straightforward card-based casino gameplay."
  },
  {
    name: "Three Card Poker",
    file: "tcp-fin.png",
    category: "tables",
    description: "Three-card poker in a dedicated interactive casino environment."
  },
  {
    name: "Ultimate Texas Hold'em",
    file: "ultimateth.png",
    category: "tables",
    description: "A digital presentation of the popular casino poker variant."
  },

  {
    name: "Mines",
    file: "minesregen.png",
    category: "originals",
    description: "An interactive tile-selection experience with escalating risk."
  },
  {
    name: "Plinko",
    file: "plinkoregen.png",
    category: "originals",
    description: "A visually engaging ball-drop game with multiple possible outcomes."
  },
  {
    name: "Bingo",
    file: "bingoregen.png",
    category: "originals",
    description: "A modern digital presentation of classic Bingo."
  },
  {
    name: "Case Opening",
    file: "caseopeningregen.png",
    category: "originals",
    description: "A reveal-driven game built around randomized virtual outcomes."
  },
  {
    name: "Think Fast",
    file: "thinkfastregen.png",
    category: "originals",
    description: "A reaction-focused experience that rewards quick thinking."
  },
  {
    name: "Price Discovery",
    file: "pricediscoveryregen.png",
    category: "originals",
    description: "An original game concept centered on estimation and decisions."
  },
  {
    name: "Scratch Cards",
    file: "scratchersregen.png",
    category: "originals",
    description: "Instant scratch-style reveals in a digital interface."
  },
  {
    name: "Pattern Remember",
    file: "patternremember.png",
    category: "originals",
    description: "A memory-based challenge centered on recognizing visual patterns."
  },
  {
    name: "Keno",
    file: "kenoregen.png",
    category: "originals",
    description: "A digital number-selection experience."
  },
  {
    name: "Dice Range",
    file: "dicerangeregen.png",
    category: "originals",
    description: "A configurable range-based dice experience."
  },
  {
    name: "Crash",
    file: "crashregen.png",
    category: "originals",
    description: "A multiplier-based experience centered on timing."
  },

  {
    name: "Pacmania",
    file: "pacmanregen.png",
    category: "arcade",
    description: "An arcade-inspired experience built around classic maze-style gameplay."
  },
  {
    name: "Basketball",
    file: "basketballregen.png",
    category: "arcade",
    description: "Basketball-themed interactive arcade gameplay."
  },
  {
    name: "Claw Machine",
    file: "clawmachineregen.png",
    category: "arcade",
    description: "A digital experience inspired by classic arcade claw machines."
  },

  {
    name: "Gravity Well",
    file: "gravitywellregen.png",
    category: "concepts",
    description: "An original gravity-themed game experience."
  },
  {
    name: "Laser Vault",
    file: "laservaultregen.png",
    category: "concepts",
    description: "A futuristic, laser-themed original experience."
  },
  {
    name: "Magnet Drop",
    file: "magnetdropregen.png",
    category: "concepts",
    description: "An original drop-based game with a magnetic theme."
  },
  {
    name: "Naval Dice",
    file: "navaldice.png",
    category: "concepts",
    description: "An original naval-themed dice experience."
  },
  {
    name: "Strike Circuit",
    file: "strikecircuit.png",
    category: "concepts",
    description: "An original futuristic arcade-style game."
  },

  ...Array.from({ length: 10 }, (_, index) => ({
    name: `Slot Machine ${index + 1}`,
    file: `slotmachine${index + 1}.png`,
    category: "slots",
    description:
      "One of the slot machine experiences in the Ethosino game catalog."
  }))
].map((game, index) => ({
  ...game,
  id: game.file,
  number: index + 1,
  image: r2Image(game.file)
}));

const categories = [
  { id: "all", label: "All Games" },
  { id: "tables", label: "Table Games" },
  { id: "originals", label: "Originals" },
  { id: "arcade", label: "Arcade" },
  { id: "concepts", label: "Original Concepts" },
  { id: "slots", label: "Slots" }
];

const capabilities = [
  {
    icon: Gamepad2,
    label: "FIRST-PARTY GAMES",
    value: "37 Game Screens",
    description: "Originals, table games, arcade and slots."
  },
  {
    icon: Network,
    label: "MULTI-TENANT",
    value: "Independent Brands",
    description: "Shared infrastructure, distinct operators."
  },
  {
    icon: WalletCards,
    label: "OPERATING SYSTEM",
    value: "Connected Controls",
    description: "Wallets, risk, payments and administration."
  },
  {
    icon: BrainCircuit,
    label: "OPERATOR INTELLIGENCE",
    value: "AI-Assisted",
    description: "Permissioned operational insights."
  }
];

/* =====================================================
   MAIN COMPONENT
   ===================================================== */

const EthosinoDemoSection = () => {
  const sliderRef = useRef(null);
  const thumbnailsRef = useRef(null);
  const mainImageRef = useRef(null);

  const [category, setCategory] = useState("all");
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const filteredGames = useMemo(() => {
    if (category === "all") return games;

    return games.filter(game => game.category === category);
  }, [category]);

  const activeGame = filteredGames[activeIndex] || filteredGames[0];

  const selectCategory = nextCategory => {
    setCategory(nextCategory);
    setActiveIndex(0);
    setFullscreenOpen(false);
  };


  const goToSlide = index => {
    // Change the selected screenshot
    sliderRef.current?.slickGoTo(index);

    // Only scroll to the main image on mobile
    if (window.matchMedia("(max-width: 767px)").matches) {
      const target = mainImageRef.current;

      if (!target) return;

      // Account for the fixed/sticky navigation header
      const headerOffset = 90;

      const targetPosition =
        target.getBoundingClientRect().top +
        window.scrollY -
        headerOffset;

      window.scrollTo({
        top: Math.max(0, targetPosition),
        behavior: window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches ? "auto" : "smooth"
      });
    }
  };

  const goPrevious = () => {
    sliderRef.current?.slickPrev();
  };

  const goNext = () => {
    sliderRef.current?.slickNext();
  };

  useEffect(() => {
    if (!thumbnailsRef.current) return;

    const activeThumbnail =
      thumbnailsRef.current.querySelector(
        `[data-game-index="${activeIndex}"]`
      );

    activeThumbnail?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center"
    });
  }, [activeIndex, category]);

  useEffect(() => {
    if (!fullscreenOpen) return;

    const onKeyDown = event => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setActiveIndex(current =>
          (current - 1 + filteredGames.length) % filteredGames.length
        );
      }

      if (event.key === "ArrowRight") {
        event.preventDefault();
        setActiveIndex(current =>
          (current + 1) % filteredGames.length
        );
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [fullscreenOpen, filteredGames.length]);

  const sliderSettings = {
    dots: false,
    arrows: false,
    infinite: filteredGames.length > 1,
    speed: 650,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    cssEase: "cubic-bezier(0.22, 1, 0.36, 1)",
    autoplay: false,
    pauseOnHover: true,
    adaptiveHeight: false,
    accessibility: true,
    beforeChange: (_, next) => setActiveIndex(next)
  };

  const formattedIndex = String(activeIndex + 1).padStart(2, "0");
  const formattedCount = String(filteredGames.length).padStart(2, "0");
  const width = useWindowWidth();
  const navigateFullscreen = direction => {
    const next =
      (activeIndex + direction + filteredGames.length) %
      filteredGames.length;

    setActiveIndex(next);
    sliderRef.current?.slickGoTo(next);
  };

  return (
    <section className="eth-showcase" id="ethosino-product-gallery">
      <div className="eth-showcase__ambient" aria-hidden="true">
        <div className="eth-showcase__glow eth-showcase__glow--one" />
        <div className="eth-showcase__glow eth-showcase__glow--two" />
        <div className="eth-showcase__grid" />
      </div>

      <div className="eth-showcase__shell">

        {/* HEADER */}

        <header className="eth-showcase__header">
          <div className="eth-showcase__header-main">
            <span className="eth-showcase__eyebrow">
              <span className="eth-showcase__eyebrow-dot" />
              EXPLORE THE PRODUCT
            </span>

            <h2>
              Built to play.
              <span> Built to operate.</span>
            </h2>

            <p>
              Explore the first-party games and experiences
              built into the Ethosino operating platform.
            </p>
          </div>

          <div className="eth-showcase__header-side">
            <span>THE PRODUCT, NOT THE PITCH</span>
            <strong>{games.length} GAME SCREENS</strong>
            <a
              href="https://demo.ethosino.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              Explore live platform
              <ArrowRight size={16} />
            </a>
          </div>
        </header>

        {/* CATEGORY FILTERS */}

        <div
          className="eth-showcase__filters"
          role="group"
          aria-label="Filter game screenshots"
        >
          {categories.map(item => {
            const count = item.id === "all"
              ? games.length
              : games.filter(game => game.category === item.id).length;

            return (
              <button
                type="button"
                key={item.id}
                className={`eth-showcase__filter ${category === item.id ? "is-active" : ""
                  }`}
                onClick={() => selectCategory(item.id)}
                aria-pressed={category === item.id}
              >
                {item.label}
                <span>{count}</span>
              </button>
            );
          })}
        </div>

        {/* MAIN SHOWCASE FRAME */}

        <div className="eth-showcase__console">
          <div className="eth-showcase__console-top">
            <div className="eth-showcase__window-controls">
              <i /><i /><i />
            </div>

            <span className="eth-showcase__console-label">
              ETHOSINO / PRODUCT_LIBRARY
            </span>

            <span className="eth-showcase__console-status">
              <i />
              PRODUCT GALLERY
            </span>
          </div>

          <div className="eth-showcase__main">

            {/* SCREENSHOT SIDE */}

            <div className="eth-showcase__visual">
              <div
                className="eth-showcase__slider-stage"
                ref={mainImageRef}
              >
                <Slider
                  key={category}
                  ref={sliderRef}
                  {...sliderSettings}
                  className="eth-showcase__slider"
                >
                  {filteredGames.map(game => (
                    <div key={game.id}>
                      <div className="eth-showcase__slide">
                        <img
                          src={game.image}
                          alt={`${game.name} screenshot`}
                          loading="lazy"
                          decoding="async"
                          draggable={false}
                        />
                      </div>
                    </div>
                  ))}
                </Slider>

                <div className="eth-showcase__image-overlay" />

                <span className="eth-showcase__image-badge">
                  <Sparkles size={13} />
                  PRODUCT SCREENSHOT
                </span>

                <button
                  type="button"
                  className="eth-showcase__expand"
                  aria-label={`Enlarge ${activeGame.name} screenshot`}
                  onClick={() => setFullscreenOpen(true)}
                >
                  <Maximize2 size={19} />
                  <span>EXPAND IMAGE</span>
                </button>
              </div>

              {/* SCREENSHOT NAVIGATION */}

              <div className="eth-showcase__visual-bottom">
                <div className="eth-showcase__progress">
                  <span>{formattedIndex}</span>
                  <div className="eth-showcase__progress-track">
                    <div
                      className="eth-showcase__progress-fill"
                      style={{
                        width: `${((activeIndex + 1) / filteredGames.length) * 100
                          }%`
                      }}
                    />
                  </div>
                  <span>{formattedCount}</span>
                </div>

                <div className="eth-showcase__arrows">
                  <button
                    type="button"
                    onClick={goPrevious}
                    aria-label="Previous game"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <button
                    type="button"
                    onClick={goNext}
                    aria-label="Next game"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            </div>

            {/* DETAILS SIDE */}

            <aside className="eth-showcase__details" aria-live="polite">
              <div className="eth-showcase__details-top">
                <span className="eth-showcase__details-index">
                  COLLECTION / {formattedIndex}
                </span>
                <span className="eth-showcase__details-category">
                  {
                    categories.find(
                      item => item.id === activeGame.category
                    )?.label
                  }
                </span>
              </div>

              <div
                className="eth-showcase__details-content"
                key={activeGame.id}
              >
                <div className="eth-showcase__details-icon">
                  <Gamepad2 size={25} />
                </div>

                <h3>{activeGame.name}</h3>
                <p>{activeGame.description}</p>

                <div className="eth-showcase__details-divider" />

                <div className="eth-showcase__details-points">
                  <div>
                    <Check size={15} />
                    <span>Integrated into the Ethosino game catalog</span>
                  </div>

                  <div>
                    <Check size={15} />
                    <span>Part of the connected player experience</span>
                  </div>

                  <div>
                    <Check size={15} />
                    <span>Presented within a tenant-aware platform</span>
                  </div>
                </div>
              </div>

              <div className="eth-showcase__details-actions">
                <button
                  type="button"
                  onClick={() => setFullscreenOpen(true)}
                  className="eth-showcase__details-preview"
                >
                  <Expand size={16} />
                  View Larger
                </button>

                <a
                  href="https://demo.ethosino.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="eth-showcase__details-demo"
                >
                  LIVE PLATFORM
                  <ArrowRight size={17} />
                </a>
              </div>
            </aside>
          </div>

          {/* THUMBNAIL RAIL */}

          <div className="eth-showcase__rail">
            <div className="eth-showcase__rail-header">
              <div>
                <span>SCREENSHOT LIBRARY</span>
                <strong>
                  Select a game to explore
                </strong>
              </div>

              <span>
                {formattedCount} SCREENS
              </span>
            </div>

            <div
              className="eth-showcase__thumbnails"
              ref={thumbnailsRef}
            >
              {filteredGames.map((game, index) => (
                <button
                  key={game.id}
                  type="button"
                  data-game-index={index}
                  className={`eth-showcase__thumbnail ${activeIndex === index ? "is-active" : ""
                    }`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Show ${game.name}`}
                  aria-pressed={activeIndex === index}
                >
                  <div className="eth-showcase__thumbnail-image">
                    <img
                      src={game.image}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  <span>{game.name}</span>

                  <i className="eth-showcase__thumbnail-indicator" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* CAPABILITY SUMMARY */}

        {width >= 825 ? <div className="eth-showcase__capabilities">
          {capabilities.map(item => {
            const Icon = item.icon;

            return (
              <div
                className="eth-showcase__capability"
                key={item.label}
              >
                <div className="eth-showcase__capability-icon">
                  <Icon size={21} />
                </div>

                <span>{item.label}</span>
                <strong>{item.value}</strong>
                <p>{item.description}</p>
              </div>
            );
          })}
        </div> : null}

        {/* BOTTOM CTA */}

        <div className="eth-showcase__footer">
          <div>
            <span>ONE CONNECTED OPERATING SYSTEM</span>
            <h3>There's more behind every screen.</h3>
            <p>
              Explore the games, operator administration,
              wallets, analytics and infrastructure together.
            </p>
          </div>

          <Link
            to="/book-demo"
            className="eth-showcase__footer-cta"
          >
            BOOK A PLATFORM DEMO
            <ArrowRight size={18} />
          </Link>
        </div>

        {/* FULLSCREEN PREVIEW */}

        <Modal
          open={fullscreenOpen}
          onClose={() => setFullscreenOpen(false)}
          center
          blockScroll
          classNames={{
            root: "eth-showcase-modal-root",
            overlay: "eth-showcase-modal-overlay",
            modal: "eth-showcase-modal",
            closeButton: "eth-showcase-modal-close"
          }}
        >
          {activeGame && (
            <div className="eth-showcase-modal__content">
              <div className="eth-showcase-modal__header">
                <div>
                  <span>ETHOSINO PRODUCT LIBRARY</span>
                  <h3>{activeGame.name}</h3>
                </div>

                <span>
                  {formattedIndex} / {formattedCount}
                </span>
              </div>

              <div className="eth-showcase-modal__image">
                <img
                  src={activeGame.image}
                  alt={`${activeGame.name} large preview`}
                />
              </div>

              <div className="eth-showcase-modal__footer">
                <button
                  type="button"
                  onClick={() =>
                    setActiveIndex(current =>
                      (current - 1 + filteredGames.length) %
                      filteredGames.length
                    )
                  }
                >
                  <ChevronLeft size={18} />
                  Previous
                </button>

                <p>{activeGame.description}</p>

                <button
                  type="button"
                  onClick={() =>
                    setActiveIndex(current =>
                      (current + 1) % filteredGames.length
                    )
                  }
                >
                  Next
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </section>
  );
};

export default EthosinoDemoSection;
